import React, { useEffect, useRef, useState } from 'react';
import { Tooltip, Typography, Popover, Slider, Grid, Box, IconButton } from '@mui/material';
import FlareIcon from '@mui/icons-material/Flare';
import AddIcon from '@mui/icons-material/Add';
import { useProjectData } from '../MattoState';
import { BarIconButton } from './ButtonStyles';
import { invalidate } from '@react-three/fiber';
import { setProfileFirstAction } from '../libs/util';
import { ProfileFirstActions } from '../../../../packages/ts-interfaces/index.ts';
import { useLightingStore, updateThreeJsLight, cleanupLightingService } from '../libs/LightingService';

// Common input styles
const inputStyles = {
  width: '48px',
  padding: '2px 4px',
  fontSize: '11px',
  textAlign: 'center',
  border: '1px solid #ccc',
  borderRadius: '6px',
  fontFamily: '"Quicksand", sans-serif',
  outline: 'none',
  transition: 'border-color 0.2s ease',
  '&:focus': {
    borderColor: '#999',
  },
  '&:hover': {
    borderColor: '#999',
  },
};

export function LightingButton() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const projectData = useProjectData();
  const setProjectData = useProjectData((state) => state.set);

  const [xy, setXY] = useState({
    x: projectData.lighting.directional || 0,
    y: projectData.lighting.directionalY || 0,
  });
  useEffect(
    () =>
      setXY({
        x: projectData.lighting.directional || 0,
        y: projectData.lighting.directionalY || 0,
      }),
    [projectData.lighting.directional, projectData.lighting.directionalY]
  );

  // Initialize lighting store with project data
  useEffect(() => {
    useLightingStore.getState().setRawValues(
      projectData.lighting.directional || 0,
      projectData.lighting.directionalY || 0
    );
  }, []);

  const updateLight = (x, y) => {
    // Update lighting store with new values
    useLightingStore.getState().setRawValues(x, y);
    
    // Only update ThreeJS light if not dragging (dragging updates are handled in the store)
    if (!useLightingStore.getState().isDragging) {
      updateThreeJsLight();
    }
    
    // Invalidate to trigger a re-render
    invalidate();
  };

  const animationFrame = useRef(null);
  const targetValues = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const [snapMode, setSnapMode] = useState(false);

  // Cleanup animation frame and lighting service on unmount
  useEffect(() => {
    return () => {
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
      cleanupLightingService();
    };
  }, []);

  const updateLighting = () => {
    const { x, y } = targetValues.current;
    updateLight(x, y);
    setProjectData((s) => {
      s.lighting.directional = x;
      s.lighting.directionalY = y;
    });
    animationFrame.current = null;
  };

  const handleMove = (x, y) => {
    // Update knob position immediately
    setXY({ x, y });
    
    // Update target values
    targetValues.current = { x, y };

    // If dragging, update immediately without animation frame
    if (isDragging.current) {
      updateLight(x, y);
      setProjectData((s) => {
        s.lighting.directional = x;
        s.lighting.directionalY = y;
      });
    } else {
      // Schedule a single lighting update if none is pending
      if (!animationFrame.current) {
        animationFrame.current = requestAnimationFrame(updateLighting);
      }
    }
  };

  // Add mouse event handlers for the XY pad container
  const handlePointerDown = (e) => {
    isDragging.current = true;
    useLightingStore.getState().setDragging(true);
    // Add event listeners for the entire document
    document.addEventListener('pointermove', handleDocumentPointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  };

  const handleDocumentPointerMove = (e) => {
    if (!isDragging.current) return;
    
    // Get the XY pad element
    const pad = e.currentTarget.querySelector('.xy-pad');
    if (!pad) return;

    // Get the pad's bounding rectangle
    const rect = pad.getBoundingClientRect();
    
    // Calculate center and radius
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const radius = (rect.width * 0.8) / 2; // 80% of the width/height
    
    // Calculate distance from center
    const dx = e.clientX - centerX;
    const dy = e.clientY - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // If outside the circle, project onto the circle
    let x, y;
    if (distance > radius) {
      const angle = Math.atan2(dy, dx);
      x = centerX + radius * Math.cos(angle);
      y = centerY + radius * Math.sin(angle);
    } else {
      x = e.clientX;
      y = e.clientY;
    }
    
    // Convert to -95 to 95 range
    const normalizedX = ((x - centerX) / radius) * 95;
    const normalizedY = ((y - centerY) / radius) * 95;
    
    // Update values
    handleMove(normalizedX, normalizedY);
  };

  const handlePointerUp = () => {
    isDragging.current = false;
    useLightingStore.getState().setDragging(false);
    // Remove document event listeners
    document.removeEventListener('pointermove', handleDocumentPointerMove);
    document.removeEventListener('pointerup', handlePointerUp);
  };

  // Cleanup event listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('pointermove', handleDocumentPointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);

  const handleCommit = (x, y) => {
    setProjectData((s) => {
      s.lighting.directional = x;
      s.lighting.directionalY = y;
    });
  };

  return (
    <>
      <Tooltip title="Lighting" arrow>
        <BarIconButton
          size="large"
          onClick={(e) => {
            setProfileFirstAction(ProfileFirstActions.OPEN_LIGHTING);
            setAnchorEl(e.currentTarget);
            setOpen((o) => !o);
          }}
        >
          <FlareIcon sx={{ color: '#000' }} />
        </BarIconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            p: 0.5,
            px: 1.5,
            background: '#ebebeb',
            borderRadius: 1,
          }}
        >
          <Box>
            <Box display="flex" flexDirection="column">
              <LightingSlider
                label="Temperature"
                min={2000}
                max={17500}
                step={50}
                value={projectData.lighting.colorTemperature}
                onChange={(_, v) => setProjectData((s) => { s.lighting.colorTemperature = v; })}
              />
              <LightingSlider
                label="Intensity"
                min={0}
                max={12}
                step={0.01}
                value={projectData.lighting.directionalIntensity}
                onChange={(_, v) => setProjectData((s) => { s.lighting.directionalIntensity = v; })}
              />
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography sx={{ fontSize: 10, color: '#737373', minWidth: '60px' }}>Position:</Typography>
              <PositionInput x={xy.x} y={xy.y} onChange={handleMove} />
              <Tooltip title={snapMode ? "Disable axis snap" : "Enable axis snap"}>
                <IconButton 
                  size="small" 
                  onClick={() => setSnapMode(!snapMode)}
                  sx={{ 
                    bgcolor: snapMode ? 'rgba(0,0,0,0.1)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' },
                    padding: '2px',
                    marginLeft: '12px'
                  }}
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="center" sx={{ py: 0.25 }}>
            <div 
              onPointerDown={handlePointerDown}
              style={{ 
                position: 'relative',
                width: '75px',
                height: '75px',
              }}
            >
              <div style={{
                position: 'absolute',
                top: '10%',
                left: '10%',
                width: '80%',
                height: '80%',
                border: '1px dashed rgba(0,0,0,0.3)',
                borderRadius: '50%',
                pointerEvents: 'none'
              }} />
              <XYPad
                min={-95}
                max={95}
                value={xy}
                onChange={handleMove}
                onChangeCommitted={handleCommit}
                size={75}
                snapMode={snapMode}
              />
            </div>
          </Box>
        </Box>
      </Popover>
    </>
  );
}

function LightingSlider({ label, min, max, step, value, onChange }) {
  const [inputValue, setInputValue] = useState(value);

  // Update input value when slider value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Only update if it's a valid number
    const numValue = parseFloat(newValue);
    if (!isNaN(numValue) && numValue >= min && numValue <= max) {
      onChange(null, numValue);
    }
  };

  const handleInputBlur = () => {
    // Reset to last valid value if input is invalid
    if (isNaN(inputValue) || inputValue < min || inputValue > max) {
      setInputValue(value);
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={1} mb={0.05}>
      <Typography sx={{ fontSize: 10, color: '#737373', minWidth: '60px' }}>{label}:</Typography>
        <Slider
        orientation="horizontal"
          size="small"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={onChange}
        sx={{ 
          width: 100, 
          color: '#000',
          '& .MuiSlider-thumb': {
            width: 8,
            height: 8,
            '&:hover': {
              boxShadow: '0px 0px 0px 6px rgba(0, 0, 0, 0.16)',
            },
            '&.Mui-focusVisible': {
              boxShadow: '0px 0px 0px 6px rgba(0, 0, 0, 0.16)',
            },
          }
        }}
      />
      <input
        type="number"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        min={min}
        max={max}
        step={step}
        style={inputStyles}
      />
    </Box>
  );
}

function PositionInput({ x, y, onChange }) {
  const [xInput, setXInput] = useState(x);
  const [yInput, setYInput] = useState(y);

  // Update input values when position changes
  useEffect(() => {
    setXInput(Math.round(x));
    setYInput(Math.round(y));
  }, [x, y]);

  const handleXChange = (e) => {
    const newValue = e.target.value;
    setXInput(newValue);
    
    const numValue = parseFloat(newValue);
    if (!isNaN(numValue) && numValue >= -95 && numValue <= 95) {
      onChange(numValue, y);
    }
  };

  const handleYChange = (e) => {
    const newValue = e.target.value;
    setYInput(newValue);
    
    const numValue = parseFloat(newValue);
    if (!isNaN(numValue) && numValue >= -95 && numValue <= 95) {
      onChange(x, numValue);
    }
  };

  const handleBlur = () => {
    // Reset to last valid values if inputs are invalid
    if (isNaN(xInput) || xInput < -95 || xInput > 95) {
      setXInput(Math.round(x));
    }
    if (isNaN(yInput) || yInput < -95 || yInput > 95) {
      setYInput(Math.round(y));
    }
  };

  return (
    <Box display="flex" gap={1}>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography sx={{ fontSize: 10, color: '#737373' }}>X:</Typography>
        <input
          type="number"
          value={xInput}
          onChange={handleXChange}
          onBlur={handleBlur}
          min={-95}
          max={95}
          style={{
            ...inputStyles,
            width: '38px',
          }}
        />
      </Box>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography sx={{ fontSize: 10, color: '#737373' }}>Y:</Typography>
        <input
          type="number"
          value={yInput}
          onChange={handleYChange}
          onBlur={handleBlur}
          min={-95}
          max={95}
          style={{
            ...inputStyles,
            width: '38px',
          }}
        />
      </Box>
    </Box>
  );
}

function XYPad({ min, max, value, onChange, onChangeCommitted, size = 100, snapMode = false }) {
  const ref = useRef(null);
  const [local, setLocal] = useState(value);
  const [drag, setDrag] = useState(false);
  const [hover, setHover] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const currentAxis = useRef(null); // Track which axis we're currently on
  const lastPosition = useRef({ x: 0, y: 0 }); // Track last position for axis switching

  // Define reference points (in degrees)
  const referencePoints = [
    { angle: null, label: 'Center' }, // Center point
    { angle: 0, label: 'Right' },
    { angle: 45, label: 'Top Right' },
    { angle: 90, label: 'Top' },
    { angle: 135, label: 'Top Left' },
    { angle: 180, label: 'Left' },
    { angle: 225, label: 'Bottom Left' },
    { angle: 270, label: 'Bottom' },
    { angle: 315, label: 'Bottom Right' },
  ];

  // Convert angle to x,y coordinates
  const angleToXY = (angle) => {
    if (angle === null) return { x: 0, y: 0 }; // Center point
    const radians = (angle * Math.PI) / 180;
    const radius = 95 * 0.7; // Using 70% of max radius
    const x = Math.cos(radians) * radius;
    const y = Math.sin(radians) * radius;
    return { x, y };
  };

  useEffect(() => setLocal(value), [value]);

  const norm = (v) => (v - min) / (max - min);
  const den = (n) => min + n * (max - min);
  const toVal = (x, y) => {
    const r = ref.current.getBoundingClientRect();
    const centerX = r.left + r.width / 2;
    const centerY = r.top + r.height / 2;
    const dx = x - centerX;
    const dy = y - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const radius = r.width / 2;
    
    // If outside the circle, project onto the circle
    let nx, ny;
    if (distance > radius) {
      const angle = Math.atan2(dy, dx);
      nx = (Math.cos(angle) + 1) / 2;
      ny = (Math.sin(angle) + 1) / 2;
    } else {
      nx = (dx / radius + 1) / 2;
      ny = (dy / radius + 1) / 2;
    }

    // Apply snap mode if enabled
    if (snapMode) {
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      const deadZone = radius * 0.1; // 10% of radius as dead zone
      
      // If we're in the dead zone and no axis is set, don't move
      if (absDx < deadZone && absDy < deadZone && !currentAxis.current) {
        return { x: 0, y: 0 };
      }

      // If we have a current axis, stick to it unless we move significantly in the other direction
      if (currentAxis.current) {
        const switchThreshold = radius * 0.3; // 30% of radius as threshold for switching
        
        if (currentAxis.current === 'x') {
          if (absDy > switchThreshold && absDy > absDx * 1.5) {
            currentAxis.current = 'y';
            nx = 0.5;
          } else {
            ny = 0.5;
          }
        } else { // y axis
          if (absDx > switchThreshold && absDx > absDy * 1.5) {
            currentAxis.current = 'x';
            ny = 0.5;
          } else {
            nx = 0.5;
          }
        }
      } else {
        // Initial axis determination
        if (absDx > absDy) {
          currentAxis.current = 'x';
          ny = 0.5;
        } else {
          currentAxis.current = 'y';
          nx = 0.5;
        }
      }
    }
    
    return { x: den(nx), y: den(1 - ny) };
  };

  // Check if point is near a reference point
  const findNearestReferencePoint = (x, y) => {
    const snapThreshold = 15; // Distance threshold for snapping
    const currentAngle = Math.atan2(y, x) * (180 / Math.PI);
    
    let nearestPoint = null;
    let minDistance = snapThreshold;
    
    for (const point of referencePoints) {
      const refXY = angleToXY(point.angle);
      const distance = Math.sqrt(
        Math.pow(x - refXY.x, 2) + Math.pow(y - refXY.y, 2)
      );
      
      if (distance < minDistance) {
        minDistance = distance;
        nearestPoint = point;
      }
    }
    
    return nearestPoint ? angleToXY(nearestPoint.angle) : null;
  };

  const pointerDown = (e) => {
    ref.current.setPointerCapture(e.pointerId);
    setDrag(true);
    useLightingStore.getState().setDragging(true);
    currentAxis.current = null; // Reset axis on new drag
    const v = toVal(e.clientX, e.clientY);
    
    // Only check for snapping on initial click
    const snappedPoint = findNearestReferencePoint(v.x, v.y);
    if (snappedPoint) {
      setLocal(snappedPoint);
      onChange(snappedPoint.x, snappedPoint.y);
    } else {
      setLocal(v);
      onChange(v.x, v.y);
    }
  };

  const pointerMove = (e) => {
    if (!drag) {
      // Check for hover state when not dragging
      const v = toVal(e.clientX, e.clientY);
      const nearestPoint = findNearestReferencePoint(v.x, v.y);
      setHoveredPoint(nearestPoint ? v : null);
      return;
    }
    
    // During drag, just update position without snapping
    const v = toVal(e.clientX, e.clientY);
    setLocal(v);
    onChange(v.x, v.y);
  };

  const pointerUp = (e) => {
    if (!ref.current.hasPointerCapture(e.pointerId)) return;
    ref.current.releasePointerCapture(e.pointerId);
    setDrag(false);
    useLightingStore.getState().setDragging(false);
    currentAxis.current = null; // Reset axis when drag ends
    onChangeCommitted(local.x, local.y);
  };

  const pointerLeave = () => {
    setHoveredPoint(null);
  };

  const knobStyle = {
    position: 'absolute',
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: '#000',
    transform: 'translate(-50%, -50%)',
    left: `${norm(local.x) * 100}%`,
    top: `${(1 - norm(local.y)) * 100}%`,
    pointerEvents: 'none',
    boxShadow: hover || drag ? '0 0 0 5px rgba(0,0,0,.12)' : '0 0 3px rgba(0,0,0,.3)',
    transition: 'box-shadow .1s',
  };

  return (
    <div
      ref={ref}
      onPointerDown={pointerDown}
      onPointerMove={pointerMove}
      onPointerUp={pointerUp}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => {
        setHover(false);
        pointerLeave();
      }}
      style={{
        position: 'relative',
        width: size,
        height: size,
        background: '#ccc',
        border: '1px solid #999',
        borderRadius: '50%',
        cursor: drag ? 'grabbing' : 'pointer',
        touchAction: 'none',
      }}
    >
      {/* Center crosshair */}
      <div style={{ 
        position: 'absolute', 
        top: '50%', 
        left: '50%', 
        width: '100%', 
        height: 1, 
        background: 'rgba(0,0,0,.2)',
        transform: 'translate(-50%, 0)',
      }} />
      <div style={{ 
        position: 'absolute', 
        top: '50%', 
        left: '50%', 
        width: 1, 
        height: '100%', 
        background: 'rgba(0,0,0,.2)',
        transform: 'translate(0, -50%)',
      }} />

      {/* Snap mode guides */}
      {snapMode && (
        <>
          {/* X-axis guide */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '0',
            width: '100%',
            height: '2px',
            background: 'rgba(0,0,0,0.3)',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }} />
          {/* Y-axis guide */}
          <div style={{
            position: 'absolute',
            top: '0',
            left: '50%',
            width: '2px',
            height: '100%',
            background: 'rgba(0,0,0,0.3)',
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
          }} />
          {/* Center point highlight */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '8px',
            height: '8px',
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
          }} />
        </>
      )}

      {/* Reference points */}
      {referencePoints.map((point) => {
        const { x, y } = angleToXY(point.angle);
        const isHovered = hoveredPoint && 
          Math.abs(hoveredPoint.x - x) < 15 && 
          Math.abs(hoveredPoint.y - y) < 15;
        
        return (
          <div
            key={point.angle ?? 'center'}
            style={{
              position: 'absolute',
              width: isHovered ? 6 : 4,
              height: isHovered ? 6 : 4,
              borderRadius: '50%',
              background: 'white',
              border: isHovered 
                ? '1.5px solid rgba(0,0,0,0.5)' 
                : '1px solid rgba(0,0,0,0.2)',
              transform: 'translate(-50%, -50%)',
              left: `${norm(x) * 100}%`,
              top: `${(1 - norm(y)) * 100}%`,
              pointerEvents: 'none',
              transition: 'all 0.1s ease',
              boxShadow: isHovered ? '0 0 3px rgba(0,0,0,0.3)' : 'none',
            }}
          />
        );
      })}
      <div style={knobStyle} />
    </div>
  );
}